import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/DailyView.css';

function DailyView({ 
  currentDate, 
  sessions, 
  availableHours, 
  onSessionSelect, 
  onCreateSession, 
  isInSeason,
  isAdmin,
  onDeleteSession,
  onUnblockSession,
  onReopenSession,  // ← AJOUTER cette ligne
  participants = []
}) {
  const { dateParam } = useParams();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [customHours, setCustomHours] = useState([]);
  
  // Récupérer la date à partir du paramètre d'URL
  const date = dateParam ? new Date(dateParam) : currentDate;
  
  // Détermination des jours spéciaux pour juin
  const isJune = date.getMonth() === 5; // 5 = juin
  const dayOfWeek = date.getDay(); // 0 = dimanche, 3 = mercredi, 6 = samedi
  const isJuneAllowedDay = isJune && (dayOfWeek === 0 || dayOfWeek === 3 || dayOfWeek === 6);

  // Plage horaire étendue pour juin, juillet et août
  let baseHours = [...availableHours];
  if (
    (isJune && isJuneAllowedDay) ||
    date.getMonth() === 6 || // juillet
    date.getMonth() === 7    // août
  ) {
    ['17:30', '18:00', '18:30', '19:00', '19:30'].forEach(h => {
      if (!baseHours.includes(h)) baseHours.push(h);
    });
  }
  const extendedHours = baseHours
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => {
      const [ah, am] = a.split(':').map(Number);
      const [bh, bm] = b.split(':').map(Number);
      return ah !== bh ? ah - bh : am - bm;
    });

  // Vérifier si la date est dans la saison (juillet/août) ou juin mercredi/samedi/dimanche
  const inSeason = isInSeason(date) || isJuneAllowedDay;
  
  // Trouver les sessions pour cette date
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  console.log(`📅 DailyView dateStr: ${dateStr} pour ${date.toString()}`);
  const daySessions = sessions.filter(session => session.date === dateStr);
  
  // Fonction pour ouvrir les détails d'une session
  const openSessionDetails = (session) => {
    onSessionSelect(session);
    navigate(`/session/${session.id}`);
  };
  
  // Fonction pour créer une nouvelle session (SANS redirection automatique)
  const handleCreateSession = async (time) => {
    const newSessionData = {
      date: dateStr,
      time,
      isBlocked: false,
      blockReason: '',
      participantCount: 0,
      paidCount: 0
    };

    try {
      const createdSession = await onCreateSession(newSessionData);
      if (createdSession) {
        console.log(`✅ Session ${time} ouverte avec succès`);
        // PAS de redirection automatique - juste un message de succès
        // La session apparaîtra maintenant comme "Ouvert aux réservations"
      }
    } catch (err) {
      alert("Erreur lors de l'ouverture de la session : " + err.message);
    }
  };
  
  return (
    <div className="daily-view-container">
      <div className="daily-view-header">
        <button onClick={() => navigate('/')} className="action-button">
          &lt; Retour au calendrier
        </button>
        <h2>{date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
        <div className="date-navigation">
          <button 
            onClick={() => {
              const prevDate = new Date(date);
              prevDate.setDate(date.getDate() - 1);
              navigate(`/daily/${prevDate.toISOString().split('T')[0]}`);
            }}
            className="action-button"
          >
            &lt; Jour précédent
          </button>
          <button 
            onClick={() => {
              const nextDate = new Date(date);
              nextDate.setDate(date.getDate() + 1);
              navigate(`/daily/${nextDate.toISOString().split('T')[0]}`);
            }}
            className="action-button"
          >
            Jour suivant &gt;
          </button>
        </div>
      </div>
      
      {inSeason ? (
        <div className="sessions-list">
          <h3>Sessions du jour :</h3>
          {extendedHours.map(time => {
  // Toutes les sessions pour cet horaire (y compris fermées)
  const allSessionsForTime = daySessions.filter(s => s.time === time);
  const activeSessionsForTime = allSessionsForTime.filter(s => !s.isDeleted);
  const closedSession = allSessionsForTime.find(s => s.isDeleted);

  // Participants pour les sessions actives seulement
  const sessionParticipants = participants.filter(p =>
    activeSessionsForTime.some(s => s.id === p.sessionId)
  );

  // Calcul du nombre total de joueurs et de réservations
  const totalParticipants = sessionParticipants.length;
  const totalPlayers = sessionParticipants.reduce((sum, p) => {
    const playerCount = p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1;
    return sum + playerCount;
  }, 0);

  // Calcul des paiements (seulement pour l'admin)
  const totalPaid = isAdmin ? sessionParticipants.reduce((sum, p) => {
    if (p.paid) {
      const playerCount = p.playerCount && !isNaN(p.playerCount) ? parseInt(p.playerCount) : 1;
      return sum + playerCount;
    }
    return sum;
  }, 0) : 0;

  const totalUnpaid = totalPlayers - totalPaid;

  // NOUVELLE LOGIQUE pour les états
  const isBlocked = activeSessionsForTime.some(s => s.isBlocked);
  const blockType = activeSessionsForTime.find(s => s.isBlocked)?.blockType;
  const blockReason = (activeSessionsForTime.find(s => s.isBlocked) || {}).blockReason || '';

  // Déterminer le type d'affichage
  let sessionClass = '';
  let statusText = '';
  let canClick = false;

  if (closedSession) {
    sessionClass = 'closed-admin';
    statusText = 'Fermé par l\'administration';
    canClick = false;
  } else if (activeSessionsForTime.length === 0) {
    sessionClass = 'not-opened';
    statusText = 'Non ouvert';
    canClick = false;
  } else if (isBlocked) {
    if (blockType === 'group') {
      sessionClass = 'blocked-group';
      statusText = 'Réservé (Groupe privé)';
      canClick = false; // Personne ne peut cliquer, sauf si tu veux l'autoriser à l'admin
    } else {
      sessionClass = 'blocked-admin';
      statusText = 'Bloqué par l\'administration';
      canClick = isAdmin; // Seul l'admin peut cliquer
    }
  } else if (totalPlayers > 0) {
    sessionClass = 'has-participants';
    statusText = `${totalPlayers} joueur(s) (${totalParticipants} réservation(s))`;
    canClick = true;
  } else {
    sessionClass = 'empty';
    statusText = 'Ouvert aux réservations';
    canClick = true;
  }

  return (
    <div
      key={time}
      className={`session-item ${sessionClass}`}
      onClick={() => {
        if (canClick && activeSessionsForTime.length > 0) {
          openSessionDetails(activeSessionsForTime[0]);
        }
      }}
      style={{ cursor: canClick && activeSessionsForTime.length > 0 ? 'pointer' : 'default' }}
    >
      <div className="session-time">{time}</div>
      <div className="session-status">
        <div className={`status-${sessionClass}`}>{statusText}</div>
        {/* Affiche la raison du blocage si présente */}
        {isBlocked && blockReason && (
          <div className="block-reason" style={{ color: '#c00', fontSize: '0.9em' }}>
            {blockReason}
          </div>
        )}
      </div>
      
      {/* Boutons admin */}
      {isAdmin && (
        <div className="admin-controls">
          {closedSession && (
            <button
              className="add-participant-button"
              onClick={(e) => {
                e.stopPropagation();
                onReopenSession(closedSession.id);
              }}
            >
              Rouvrir
            </button>
          )}
          
          {!closedSession && activeSessionsForTime.length === 0 && (
            <button
              className="add-participant-button"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateSession(time);
              }}
            >
              Ouvrir
            </button>
          )}
          
          {!closedSession && activeSessionsForTime.length > 0 && !isBlocked && totalPlayers === 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                activeSessionsForTime.forEach(s => onDeleteSession(s.id));
              }}
              className="block-button"
              title="Fermer ce créneau"
            >
              Fermer
            </button>
          )}

          {/* Bouton pour débloquer si session bloquée */}
          {!closedSession && isBlocked && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                activeSessionsForTime.forEach(s => onUnblockSession(s.id));
              }}
              className="unblock-button"
              title="Débloquer ce créneau"
            >
              Débloquer
            </button>
          )}
        </div>
      )}
    </div>
  );
})}
          {/* Bouton d'ouverture d'une nouvelle session */}
          {isAdmin && (
            <div style={{ marginTop: 16 }}>
              <button
                className="add-participant-button"
                onClick={() => {
                  // Créer un prompt pour choisir l'heure
                  const time = prompt("Quelle heure souhaitez-vous ouvrir ? (Format HH:MM)", "20:00");
                  if (time && /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
                    handleCreateSession(time);
                  } else if (time) {
                    alert("Format d'heure invalide. Utilisez le format HH:MM (ex: 20:00)");
                  }
                }}
              >
                Ouvrir un nouveau créneau
              </button>
            </div>
          )}
          {isAdmin && (
  <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
    {['20:00', '20:30', '21:00', '21:30'].map(time => (
      <button
        key={time}
        className="action-button"
        onClick={() => {
          handleCreateSession(time);
        }}
      >
        <span role="img" aria-label="horloge">🕗</span>
        Ouvrir {time}
      </button>
    ))}
  </div>
)}
        </div>
      ) : (
        <div className="off-season-message">
          <p>
            Cette date est en dehors de la période d'ouverture (1er juillet au 31 août)
            et en juin uniquement les mercredis, samedis et dimanches.
          </p>
          <button onClick={() => navigate('/')} className="calendar-button">
            Retour au calendrier
          </button>
        </div>
      )}
    </div>
  );
}

export default DailyView;